import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  cyberGlassBackdrop,
} from '../../styles/cyberGlass';
import { gradientPillCss } from '../../styles/gradientPill';
import { useFunnelCart } from '../../context/FunnelCartProvider';

type MobileSnapshotFabProps = {
  readonly title?: string;
  readonly buttonLabel?: string;
  readonly children: ReactNode;
};

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 1080px) {
    display: block;
  }
`;

const FabButton = styled.button`
  ${gradientPillCss};
  position: fixed;
  right: 1rem;
  bottom: 5.1rem;
  z-index: 65;
  border: 0;
  appearance: none;
  min-height: auto;
  padding: 0.58rem 0.82rem;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
  cursor: pointer;
`;

const Backdrop = styled.button<{ $open: boolean }>`
  border: 0;
  margin: 0;
  padding: 0;
  position: fixed;
  inset: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  z-index: 66;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  min-height: 100vh;
  background: rgba(19, 27, 39, 0.7);
  color: ${({ theme }) => theme.colors.gray900};
  transform: translateX(${({ $open }) => ($open ? '0' : '102%')});
  transition: transform 0.22s ease;
  z-index: 67;
  backdrop-filter: blur(26px) saturate(145%);
  -webkit-backdrop-filter: blur(26px) saturate(145%);
`;

const DrawerBody = styled.div`
  height: 100%;
  overflow: hidden;
  padding:
    calc(env(safe-area-inset-top) + 3.65rem)
    0.85rem
    calc(env(safe-area-inset-bottom) + 0.9rem);
  display: grid;
  align-content: start;
  gap: 0.68rem;

  > * {
    min-height: auto !important;
  }

  > aside,
  > section {
    padding: 0.7rem 0.72rem 0.64rem;
    gap: 0.52rem;
  }

  h3 {
    font-size: clamp(1.08rem, 4.8vw, 1.26rem);
    line-height: 0.92;
  }

  p,
  li,
  span {
    font-size: 0.92rem;
    line-height: 1.24;
  }

  strong {
    font-size: 0.95rem;
    line-height: 1.16;
  }

  ul {
    padding-left: 0.95rem;
    gap: 0.12rem;
  }

  button,
  a {
    padding: 0.5rem 0.72rem;
    font-size: 0.9rem;
  }

  > section a {
    padding: 0.58rem;
    gap: 0.28rem;
  }

  @media (max-width: 380px) {
    padding:
      calc(env(safe-area-inset-top) + 3.55rem)
      0.72rem
      calc(env(safe-area-inset-bottom) + 0.82rem);
    gap: 0.58rem;

    > aside,
    > section {
      padding: 0.62rem 0.64rem 0.56rem;
      gap: 0.46rem;
    }

    h3 {
      font-size: 1rem;
    }

    p,
    li,
    span {
      font-size: 0.86rem;
      line-height: 1.18;
    }

    strong {
      font-size: 0.89rem;
    }

    button,
    a {
      padding: 0.46rem 0.64rem;
      font-size: 0.84rem;
    }

    > section a {
      padding: 0.52rem;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top) + 0.85rem);
  z-index: 2;
  border: 0;
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 999px;
  padding: 0.5rem 0.82rem;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
`;

const BackToCartButton = styled(CloseButton)`
  left: 0.9rem;
  right: auto;
`;

const SnapshotCloseButton = styled(CloseButton)`
  right: 0.9rem;
`;

export default function MobileSnapshotFab({
  title = 'Selection Snapshot',
  buttonLabel = 'Snapshot',
  children,
}: MobileSnapshotFabProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalQuantity, openDrawer } = useFunnelCart();
  const [isOpen, setIsOpen] = useState(false);
  const hasCartItems = totalQuantity > 0;

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mobileSnapshot') !== 'open') return;

    setIsOpen(true);
    params.delete('mobileSnapshot');

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  const handleTriggerClick = () => {
    if (hasCartItems) {
      openDrawer();
      return;
    }

    setIsOpen(true);
  };

  const handleBackToCart = () => {
    setIsOpen(false);
    window.setTimeout(() => {
      openDrawer();
    }, 0);
  };

  return (
    <MobileOnly>
      <FabButton type="button" onClick={handleTriggerClick}>
        {hasCartItems ? 'Cart' : buttonLabel}
      </FabButton>
      <Backdrop type="button" aria-label="Close snapshot" $open={isOpen} onClick={() => setIsOpen(false)} />
      <Drawer $open={isOpen} aria-hidden={!isOpen} aria-label={title}>
        {hasCartItems ? (
          <BackToCartButton type="button" onClick={handleBackToCart}>
            Back to cart
          </BackToCartButton>
        ) : null}
        <SnapshotCloseButton type="button" onClick={() => setIsOpen(false)}>
          Close
        </SnapshotCloseButton>
        <DrawerBody>{children}</DrawerBody>
      </Drawer>
    </MobileOnly>
  );
}
